/**
 * Email helper utilities — parsing, deduping, and validating addresses
 * from Gmail thread headers.
 */

/** Extract clean lowercase email from "Name <foo@bar.com>" or "foo@bar.com". */
export function parseAddress(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const angle = raw.match(/<([^>]+)>/);
  const candidate = angle ? angle[1] : raw;
  const m = candidate.match(/[\w.+-]+@[\w-]+\.[\w.-]+/);
  return m ? m[0].toLowerCase().trim() : null;
}

/** Display label "Name" or fallback to email. */
export function addressLabel(raw) {
  if (!raw) return '';
  const m = raw.match(/^([^<]+?)\s*<.+>$/);
  if (m) return m[1].trim().replace(/^"(.*)"$/, '$1');
  return parseAddress(raw) || raw;
}

/** Split a comma/semicolon-separated header into individual entries (preserves angle-brackets). */
function splitHeader(value) {
  if (!value) return [];
  // Naive split on commas at top level (Gmail headers don't normally embed commas)
  return value.split(/,(?![^<]*>)/).map(s => s.trim()).filter(Boolean);
}

export function isValidEmail(addr) {
  return /^[\w.+-]+@[\w-]+\.[\w.-]+$/.test((addr || '').trim());
}

/**
 * From an audit doc with thread.messages[], compute:
 *   - allParticipants: unique lowercase emails seen in From/To/Cc across the whole thread
 *   - lastSender: the latest message's From (so we know who to reply to)
 *
 * Excludes the connected user's own email so they don't CC themselves.
 */
export function extractThreadParticipants(audit, ownEmail) {
  const own = (ownEmail || '').toLowerCase().trim();
  const messages = audit?.thread?.messages || [];
  const seen = new Set();
  const labels = {};   // lowercase email → display label

  let lastFromRaw = '';
  let lastDate = -Infinity;

  for (const m of messages) {
    // Track latest sender
    const ts = (() => {
      const t = m?.date && new Date(m.date).getTime();
      return Number.isFinite(t) ? t : 0;
    })();
    if (ts >= lastDate && m.from) {
      lastDate = ts;
      lastFromRaw = m.from;
    }

    for (const header of [m.from, m.to, m.cc]) {
      for (const piece of splitHeader(header)) {
        const addr = parseAddress(piece);
        if (!addr) continue;
        if (addr === own) continue;
        if (!seen.has(addr)) {
          seen.add(addr);
          labels[addr] = addressLabel(piece);
        }
      }
    }
  }

  const lastFrom = parseAddress(lastFromRaw);
  return {
    all:       Array.from(seen),
    labels,
    lastFrom:  lastFrom && lastFrom !== own ? lastFrom : null,
    lastFromLabel: lastFrom ? (labels[lastFrom] || addressLabel(lastFromRaw)) : null,
  };
}

/** Format a list of emails as a comma-separated string for the Gmail send API. */
export function joinAddresses(addrs) {
  return (addrs || []).filter(Boolean).join(', ');
}
